import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../src/utils/theme';
import { config } from '../../../src/config';
import PageFooter from '../../../src/components/PageFooter';

export default function PublicFleetSignupScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { accountNumber } = useLocalSearchParams<{ accountNumber: string }>();

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [managerName, setManagerName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [managerPhone, setManagerPhone] = useState('');
  const [estMonthlyVolume, setEstMonthlyVolume] = useState('');
  const [vehicleCount, setVehicleCount] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    if (!companyName.trim() || !billingEmail.trim() || !managerName.trim() || !managerEmail.trim()) {
      toast.show({ type: 'error', title: 'Required', message: 'Company name, billing email, manager name, and manager email are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        companyName: companyName.trim(),
        billingEmail: billingEmail.trim(),
        billingAddress: billingAddress.trim() || undefined,
        billingCity: billingCity.trim() || undefined,
        billingState: billingState.trim() || undefined,
        billingZip: billingZip.trim() || undefined,
        managerName: managerName.trim(),
        managerEmail: managerEmail.trim(),
        managerPhone: managerPhone.trim() || undefined,
        estMonthlyVolume: estMonthlyVolume ? parseInt(estMonthlyVolume, 10) : undefined,
        vehicleCount: vehicleCount ? parseInt(vehicleCount, 10) : undefined,
        notes: notes.trim() || undefined,
      };
      const res = await fetch(`${config.apiUrl}/api/public/fleet-application/${accountNumber}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message || 'Could not submit application.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <View style={styles.successContainer}>
        <Ionicons name="checkmark-circle" size={72} color={colors.success} />
        <Text style={styles.successTitle}>Application Submitted</Text>
        <Text style={styles.successText}>
          Thank you! The business will review your request and reach out to {managerEmail} once it&apos;s approved.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.brandRow}>
        <Text style={styles.brand}>RinsePay</Text>
      </View>
      <Card>
        <Text style={styles.title}>Apply for a Fleet Account</Text>
        <Text style={styles.subtitle}>Fill out the form below — the business owner will review and approve your application.</Text>

        <Text style={styles.sectionTitle}>Company</Text>
        <Input label="Company Name *" value={companyName} onChangeText={setCompanyName} placeholder="Acme Logistics LLC" />
        <Input label="Billing Email *" value={billingEmail} onChangeText={setBillingEmail} placeholder="billing@acme.com" keyboardType="email-address" autoCapitalize="none" />
        <Input label="Billing Address" value={billingAddress} onChangeText={setBillingAddress} placeholder="123 Main St" />
        <View style={styles.row}>
          <View style={{ flex: 2 }}>
            <Input label="City" value={billingCity} onChangeText={setBillingCity} placeholder="Bonne Terre" />
          </View>
          <View style={{ flex: 1 }}>
            <Input label="State" value={billingState} onChangeText={setBillingState} placeholder="MO" maxLength={2} autoCapitalize="characters" />
          </View>
          <View style={{ flex: 1 }}>
            <Input label="ZIP" value={billingZip} onChangeText={setBillingZip} placeholder="63628" keyboardType="numeric" maxLength={10} />
          </View>
        </View>

        <Text style={styles.sectionTitle}>Fleet Manager Contact</Text>
        <Input label="Name *" value={managerName} onChangeText={setManagerName} placeholder="Jane Smith" />
        <Input label="Email *" value={managerEmail} onChangeText={setManagerEmail} placeholder="jane@acme.com" keyboardType="email-address" autoCapitalize="none" />
        <Input label="Phone" value={managerPhone} onChangeText={setManagerPhone} placeholder="(555) 123-4567" keyboardType="phone-pad" />

        <Text style={styles.sectionTitle}>About Your Fleet (optional)</Text>
        <View style={styles.row}>
          <View style={{ flex: 1 }}>
            <Input label="Est. washes / month" value={estMonthlyVolume} onChangeText={setEstMonthlyVolume} placeholder="50" keyboardType="numeric" />
          </View>
          <View style={{ flex: 1 }}>
            <Input label="Vehicle count" value={vehicleCount} onChangeText={setVehicleCount} placeholder="12" keyboardType="numeric" />
          </View>
        </View>
        <Input label="Notes" value={notes} onChangeText={setNotes} placeholder="Anything else we should know" multiline />

        <Button
          title={submitting ? 'Submitting...' : 'Submit Application'}
          onPress={handleSubmit}
          loading={submitting}
          disabled={submitting}
          style={{ marginTop: spacing.lg }}
        />
      </Card>
      <View style={{ height: spacing.xxl }} />
      <PageFooter />
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  brandRow: { alignItems: 'center', marginBottom: spacing.lg, marginTop: spacing.lg },
  brand: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.primary },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs, marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md, marginBottom: spacing.sm },
  row: { flexDirection: 'row', gap: spacing.sm },
  successContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background, padding: spacing.xl },
  successTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.lg },
  successText: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.sm, textAlign: 'center', maxWidth: 360 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
